// Eval-Execution Engine — mockup-faithful presentational kit.
//
// This module is the single source of truth for the "Eval-Execution Engine"
// look shipped in docs/design/eval-execution-engine-mockup-v1.html
// (artifact c9f0368c). Both DatasetsPages.jsx (Screen 0 · Golden set) and
// EvalsPages.jsx (Screens 1-3 · Kickoff / Running / Results) render entirely
// through these atoms so the live UI matches the design spec 1:1.
//
// Tokens are lifted verbatim from theme/colors.js + theme/materials.js — the
// work-surface treatment (surface bg + border + three-layer spatial shadow,
// RADIUS.inner cards, RADIUS.deep tiles) is the mockup's `.panel` / `.tablecard`
// / `.prog` / `.hero` shape. Verdict chips use the fixed theme-independent
// pastels from the mockup's StatePill scale (readable on both themes).
//
// Pure presentation — no data fetching, no business logic (that stays in the
// tested datasetItemFormat.js / evalRunFormat.js units). Every component reads
// the mutable COLORS at render, so a theme swap re-tints without prop changes.
import React from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS, RADIUS } from "../../../theme/materials";

// ── keyframes (shimmer / spin / fade) injected once ──────────────────
if (typeof document !== "undefined" && !document.getElementById("eval-ui-kf")) {
  const s = document.createElement("style");
  s.id = "eval-ui-kf";
  s.textContent =
    "@keyframes evalShimmer{to{transform:translateX(100%)}}" +
    "@keyframes evalSpin{to{transform:rotate(360deg)}}" +
    "@keyframes evalFade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}";
  document.head.appendChild(s);
}

// Fixed pastel verdict chips — theme-independent, verbatim from the mockup's
// SharedUI STATE_COLORS. Light pastels + dark text read on dark AND light.
const V = {
  acc: { bg: "#dcfce7", fg: "#166534" }, // ACCURATE  → passed
  ina: { bg: "#fee2e2", fg: "#991b1b" }, // INACCURATE → failed
  par: { bg: "#fef3c7", fg: "#92400e" }, // PARTIAL   → counted apart
  run: { bg: "#dbeafe", fg: "#1e40af" }, // running / scoring
  err: { bg: "#e5e7eb", fg: "#4b5563" }, // ERROR     → excluded
  que: { bg: "#f1f5f9", fg: "#475569" }, // pending / queued
};

// Map a verdict / status string → one of the six pastel slots.
function slotFor(state) {
  const s = String(state || "").toLowerCase();
  if (["accurate", "passed", "completed", "succeeded", "ok"].includes(s)) return V.acc;
  if (["inaccurate", "failed"].includes(s)) return V.ina;
  if (["partial", "partially_accurate"].includes(s)) return V.par;
  if (["running", "in_progress", "scoring", "scoring…"].includes(s)) return V.run;
  if (["error", "errored"].includes(s)) return V.err;
  return V.que; // pending / queued / unknown
}

// ── Section header — the `.head` block (h2 + descriptive paragraph) ──
export function SectionHead({ title, count, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, letterSpacing: "-0.01em", color: COLORS.text, fontWeight: 700, margin: 0 }}>
        {title}
        {count != null && (
          <span style={{ color: COLORS.textDim, fontWeight: 500, fontSize: 13 }}> · {count}</span>
        )}
      </h2>
      {children && (
        <p style={{ margin: "4px 0 0", color: COLORS.textDim, fontSize: 13, maxWidth: "62ch", lineHeight: 1.5 }}>
          {children}
        </p>
      )}
    </div>
  );
}

// ── Toolbar — flex row of primary/ghost buttons + optional right hint ──
export function Toolbar({ children, style }) {
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
      marginBottom: 14, ...style,
    }}>
      {children}
    </div>
  );
}
export function Spacer() {
  return <span style={{ flex: 1 }} />;
}
export function Hint({ children, style }) {
  return (
    <span style={{ fontSize: 11.5, color: COLORS.textDim, maxWidth: "34ch", ...style }}>
      {children}
    </span>
  );
}

// ── Buttons — accent primary (w/ glow) + ghost secondary ─────────────
export function Btn({ variant = "primary", children, disabled, style, ...rest }) {
  const ghost = variant === "ghost";
  return (
    <button
      disabled={disabled}
      style={{
        font: "inherit", fontSize: 13, fontWeight: ghost ? 600 : 700,
        color: ghost ? COLORS.textMuted : "#fff",
        background: ghost ? COLORS.surface : COLORS.accent,
        border: ghost ? `1px solid ${COLORS.border}` : "none",
        padding: "9px 16px", borderRadius: 6, cursor: disabled ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 8,
        boxShadow: ghost ? "none" : `0 0 16px ${COLORS.accentGlow}`,
        opacity: disabled ? 0.6 : 1, transition: "opacity 0.15s",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Panel — bordered work-surface card (the `.panel`) ────────────────
export function Panel({ children, style }) {
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`, background: COLORS.surface,
      borderRadius: RADIUS.inner, padding: "14px 16px", marginBottom: 16,
      boxShadow: MATERIALS.spatialShadow(), ...style,
    }}>
      {children}
    </div>
  );
}

// ── Dropzone — dashed import affordance (the `.dropzone`) ────────────
export function Dropzone({ icon = "⬆", children, onClick, style }) {
  return (
    <div
      onClick={onClick}
      style={{
        border: `1.5px dashed ${COLORS.border}`, borderRadius: RADIUS.deep,
        padding: "14px 16px", display: "flex", alignItems: "center", gap: 12,
        background: COLORS.surface, fontSize: 12.5, color: COLORS.textDim,
        cursor: onClick ? "pointer" : "default", ...style,
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <div>{children}</div>
    </div>
  );
}

// ── Callout — accent-soft info box (the `.callout`) ──────────────────
export function Callout({ icon = "🧭", children, style }) {
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5,
      color: COLORS.textMuted, background: COLORS.accentSoft,
      border: `1px solid ${COLORS.accent}`, borderRadius: RADIUS.deep,
      padding: "11px 14px", marginTop: 16, ...style,
    }}>
      <span>{icon}</span>
      <div style={{ lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}
// Bold inline emphasis inside a callout / dropzone (maps `.callout b`).
export function B({ children, accent }) {
  return (
    <b style={{ color: accent ? COLORS.accentText : COLORS.text, fontWeight: 700 }}>{children}</b>
  );
}
// Monospace inline token (maps `.mono` in a callout).
export function Mono({ children }) {
  return (
    <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11.5, color: COLORS.textMuted }}>
      {children}
    </span>
  );
}

// ── Table card — bordered card + caption bar + horizontal scroll ─────
export function TableCard({ title, meta, minWidth = 660, children, style }) {
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, overflow: "hidden",
      background: COLORS.surface, boxShadow: MATERIALS.spatialShadow(), ...style,
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`,
      }}>
        <h3 style={{
          fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase",
          color: COLORS.textDim, fontWeight: 700, margin: 0,
        }}>{title}</h3>
        {meta != null && (
          <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11, color: COLORS.textDim }}>
            {meta}
          </span>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{
          width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth,
        }}>
          {children}
        </table>
      </div>
    </div>
  );
}

// Table head — `cols` are strings or {label, align}.
export function THead({ cols }) {
  return (
    <thead>
      <tr>
        {cols.map((c, i) => {
          const label = typeof c === "string" ? c : c.label;
          const align = typeof c === "string" ? "left" : (c.align || "left");
          return (
            <th key={i} style={{
              textAlign: align, fontSize: 10, letterSpacing: "0.05em",
              textTransform: "uppercase", color: COLORS.textDim, fontWeight: 700,
              padding: "8px 14px", borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "nowrap",
            }}>{label}</th>
          );
        })}
      </tr>
    </thead>
  );
}

// Clickable / hoverable row. `pending` dims it (mockup's `tr.pending`).
export function Tr({ children, onClick, pending, style }) {
  return (
    <tr
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default", transition: "background 0.15s", ...style }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.background = COLORS.surfaceHover)}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.background = "transparent")}
    >
      {React.Children.map(children, (child) =>
        pending && React.isValidElement(child)
          ? React.cloneElement(child, { _pending: true })
          : child,
      )}
    </tr>
  );
}

// Cell. `align`, `dim` (textDim), `first` (also textDim like the mockup's
// first column), `mono`, `colSpan`. `_pending` (injected by Tr) fades it.
export function Td({ children, align = "left", dim, first, mono, colSpan, _pending, style }) {
  return (
    <td colSpan={colSpan} style={{
      padding: "12px 14px", borderBottom: `1px solid ${COLORS.border}`, verticalAlign: "middle",
      textAlign: align,
      color: (dim || first) ? COLORS.textDim : COLORS.textMuted,
      opacity: _pending ? 0.6 : 1,
      fontFamily: mono ? "ui-monospace, Menlo, monospace" : undefined,
      fontVariantNumeric: mono ? "tabular-nums" : undefined,
      ...style,
    }}>
      {children}
    </td>
  );
}

// One-line truncating question cell content (mockup's `td .q`).
export function QCell({ children, maxWidth = 420 }) {
  return (
    <span style={{
      color: COLORS.text, fontWeight: 500, display: "block", maxWidth,
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ── Pills / badges ───────────────────────────────────────────────────

// Source provenance tag (`.src`) — authored / imported / from-trace. The
// trace variant gets accent tint like the mockup.
export function SourcePill({ source }) {
  const trace = source === "trace" || source === "from trace";
  const imported = source === "imported";
  const label = trace ? "from trace" : (source || "—");
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase",
      padding: "2px 7px", borderRadius: 6, whiteSpace: "nowrap",
      background: trace ? COLORS.accentSoft : COLORS.surfaceHover,
      color: (trace || imported) ? COLORS.accentText : COLORS.textDim,
    }}>{label}</span>
  );
}

// Verdict badge — bold uppercase pastel chip (`.badge v-*`).
export function VerdictBadge({ verdict }) {
  if (!verdict) return <span style={{ fontSize: 10, color: COLORS.textDim }}>pending</span>;
  const scoring = String(verdict).toLowerCase().startsWith("scor");
  const c = slotFor(verdict);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700,
      padding: "3px 8px", borderRadius: 999, letterSpacing: "0.02em",
      background: c.bg, color: c.fg,
    }}>
      {scoring && <Spinner color={c.fg} />}
      {verdict}
    </span>
  );
}

// Status pill with a leading dot (`.pill` — running / completed / queued).
export function StatusPill({ state }) {
  if (!state) return <span style={{ fontSize: 10, color: COLORS.textMuted }}>—</span>;
  const c = slotFor(state);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 600,
      padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.fg,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
      {state}
    </span>
  );
}

// Scorer pill (`.score`) — judge / numeric / exact… ; `dim` for errored.
export function ScorerPill({ children, dim }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
      padding: "2px 8px", borderRadius: 6,
      background: dim ? COLORS.surfaceHover : COLORS.accentSoft,
      color: dim ? COLORS.textDim : COLORS.accentText,
      border: `1px solid ${dim ? COLORS.border : COLORS.accent}`,
    }}>{children}</span>
  );
}

// A trace deep-link cell (`.tracelink`) or an em-dash when absent.
export function TraceLink({ traceId, onClick }) {
  const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };
  if (!traceId) return <span style={{ ...mono, color: COLORS.textDim }}>—</span>;
  const label = `${String(traceId).slice(0, 6)}…${String(traceId).slice(-2)}`;
  // No handler → nowhere to go (the turn has no Inspector-resolvable
  // conversation). Render the id as plain text rather than link-styling
  // something that does nothing when clicked.
  if (!onClick) {
    return (
      <span
        title="This turn has no Inspector trace to open."
        style={{ ...mono, color: COLORS.textDim }}
      >{label}</span>
    );
  }
  return (
    <a
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        ...mono, color: COLORS.accentText,
        textDecoration: "none", borderBottom: `1px dashed ${COLORS.accent}`, cursor: "pointer",
      }}
    >{label}</a>
  );
}

// Small spinner (scoring… badge / running button).
export function Spinner({ color = "#fff", size = 12 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%", display: "inline-block",
      border: `2px solid ${color}44`, borderTopColor: color,
      animation: "evalSpin 0.7s linear infinite",
    }} />
  );
}

// ── Back row (‹ Evals) ───────────────────────────────────────────────
export function BackButton({ label, onClick }) {
  return (
    <button onClick={onClick} style={{
      font: "inherit", fontSize: 12.5, fontWeight: 600, color: COLORS.textMuted,
      background: "transparent", border: "none", cursor: "pointer", padding: "4px 0",
      display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14,
    }}>‹ {label}</button>
  );
}

// ── Progress (Screen 2) — bar + progress line ────────────────────────
export function ProgressPanel({ pct, title, subtitle, statusChip, line }) {
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, background: COLORS.surface,
      padding: "15px 18px", marginBottom: 16, boxShadow: MATERIALS.spatialShadow(),
    }}>
      {(title || statusChip) && (
        <div style={{
          display: "flex", flexWrap: "wrap", alignItems: "center",
          justifyContent: "space-between", gap: 10, marginBottom: 12,
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13, color: COLORS.text, fontWeight: 600 }}>{title}</span>
            {subtitle && <span style={{ fontSize: 12, color: COLORS.textDim }}>{subtitle}</span>}
          </div>
          {statusChip}
        </div>
      )}
      <div style={{ height: 8, borderRadius: 999, background: COLORS.border, overflow: "hidden", position: "relative" }}>
        <div style={{
          height: "100%", borderRadius: 999, width: `${pct}%`,
          background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.cyan})`,
          position: "relative", overflow: "hidden", transition: "width 0.4s",
        }}>
          <span style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
            transform: "translateX(-100%)", animation: "evalShimmer 1.5s infinite",
          }} />
        </div>
      </div>
      {line && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 9, fontSize: 12, color: COLORS.textDim }}>
          {line}
        </div>
      )}
    </div>
  );
}
// tabular-nums emphasis inside a progress line.
export function Num({ children }) {
  return <b style={{ color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>{children}</b>;
}

// ── Counters (Screen 2) ──────────────────────────────────────────────
export function Counters({ children }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))",
      gap: 10, marginBottom: 16,
    }}>{children}</div>
  );
}
export function Counter({ label, value, color, swatch }) {
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.deep, padding: "11px 13px",
      background: COLORS.surface, boxShadow: MATERIALS.spatialShadow(),
    }}>
      <div style={{
        fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700,
        color: COLORS.textDim, display: "flex", alignItems: "center", gap: 6,
      }}>
        {swatch && <span style={{ width: 8, height: 8, borderRadius: 2, background: swatch }} />}
        {label}
      </div>
      <div style={{
        fontSize: 20, fontWeight: 750, marginTop: 3, fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.01em", color: color || COLORS.text,
      }}>{value}</div>
    </div>
  );
}

// ── Aggregate hero (Screen 3) — donut + chips + metarow ──────────────
export function Hero({ pct, frac, title, subtitle, statusChip, chips, stats }) {
  const C = 2 * Math.PI * 52; // r=52
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "auto 1fr", gap: 22, alignItems: "center",
      border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, background: COLORS.surface,
      padding: "20px 22px", marginBottom: 16, boxShadow: MATERIALS.spatialShadow(),
    }}>
      <div style={{ width: 124, height: 124, position: "relative" }}>
        <svg width="124" height="124" viewBox="0 0 124 124" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="62" cy="62" r="52" fill="none" stroke={COLORS.border} strokeWidth="13" />
          <circle
            cx="62" cy="62" r="52" fill="none" stroke={COLORS.accent} strokeWidth="13"
            strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - (frac || 0))}
          />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: COLORS.text }}>{pct}</span>
          <span style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.textDim, fontWeight: 700 }}>Accuracy</span>
        </div>
      </div>
      <div>
        <h2 style={{ fontSize: 15, marginBottom: 3, fontFamily: "ui-monospace, Menlo, monospace", color: COLORS.text }}>{title}</h2>
        <div style={{ color: COLORS.textDim, fontSize: 12.5, marginBottom: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {subtitle} {statusChip}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {chips.map((c) => (
            <span key={c.key || c.label} style={{
              display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600,
              padding: "6px 11px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
              background: COLORS.surface, color: COLORS.textMuted,
            }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: c.color }} />
              {c.label} <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 750, color: COLORS.text }}>{c.count}</span>
            </span>
          ))}
        </div>
        {stats && stats.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
            {stats.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <span style={{ fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textDim, fontWeight: 700 }}>{k}</span>
                <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: COLORS.text }}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Expected-vs-actual diff (Screen 3 expand row) ────────────────────
export function DiffRow({ expected, actual, note }) {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "10px 12px", background: COLORS.surface }}>
          <div style={{ fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700, marginBottom: 5, color: COLORS.green }}>Expected output</div>
          <p style={{ margin: 0, fontSize: 12.5, color: COLORS.text, lineHeight: 1.45 }}>{expected}</p>
        </div>
        <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "10px 12px", background: COLORS.surface }}>
          <div style={{ fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700, marginBottom: 5, color: COLORS.pink }}>Agent answer</div>
          <p style={{ margin: 0, fontSize: 12.5, color: COLORS.text, lineHeight: 1.45 }}>{actual}</p>
        </div>
      </div>
      {note && (
        <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 10 }}>{note}</div>
      )}
    </>
  );
}

// ── Results legend (Screen 3 footer) ─────────────────────────────────
export function Legend() {
  const items = [
    [<VerdictBadge key="a" verdict="ACCURATE" />, "passed"],
    [<VerdictBadge key="i" verdict="INACCURATE" />, "failed"],
    [<VerdictBadge key="p" verdict="PARTIAL" />, "counted apart"],
    [<VerdictBadge key="e" verdict="ERROR" />, "excluded from accuracy"],
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 11.5, color: COLORS.textDim, marginTop: 12, alignItems: "center" }}>
      {items.map(([badge, label], i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {badge} → {label}
        </span>
      ))}
    </div>
  );
}

// ── Styled <select> for the kickoff control (mockup's `.select`) ─────
export function Field({ label, children, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textDim }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: COLORS.textDim, maxWidth: "30ch" }}>{hint}</span>}
    </div>
  );
}
export const selectStyle = () => ({
  appearance: "none", font: "inherit", fontSize: 13, fontWeight: 600, color: COLORS.text,
  background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6,
  padding: "9px 30px 9px 11px", minWidth: 210, cursor: "pointer",
});
